import { MetricField } from '@/__generated__/graphql';
import { type I18n } from '@lingui/core';
import { t } from '@lingui/macro';

export type MetricFieldAttributes = {
  value: MetricField;
  dataLabel: string;
  lastDataLabel: string;
};

type AttributesMap = Record<MetricField, MetricFieldAttributes>;

const getAttributesMap = (i18n: I18n): AttributesMap => ({
  [MetricField.AccumulatedVol]: {
    value: MetricField.AccumulatedVol,
    dataLabel: i18n._(t`volumen acumulado`),
    lastDataLabel: i18n._(t`último volumen acumulado`),
  },
  [MetricField.AccumulatedVolume]: {
    value: MetricField.AccumulatedVolume,
    dataLabel: i18n._(t`volumen acumulado`),
    lastDataLabel: i18n._(t`último volumen acumulado`),
  },
  [MetricField.AccumulatedVolRaw]: {
    value: MetricField.AccumulatedVolRaw,
    dataLabel: i18n._(t`volumen acumulado bruto`),
    lastDataLabel: i18n._(t`último volumen acumulado bruto`),
  },
  [MetricField.Allocation]: {
    value: MetricField.Allocation,
    dataLabel: i18n._(t`dotación`),
    lastDataLabel: i18n._(t`última dotación`),
  },
  [MetricField.Angle]: {
    value: MetricField.Angle,
    dataLabel: i18n._(t`ángulo`),
    lastDataLabel: i18n._(t`último ángulo`),
  },
  [MetricField.AngleNonCalibrated]: {
    value: MetricField.AngleNonCalibrated,
    dataLabel: i18n._(t`ángulo no calibrado`),
    lastDataLabel: i18n._(t`último ángulo no calibrado`),
  },
  [MetricField.AngleNonCalibratedRaw]: {
    value: MetricField.AngleNonCalibratedRaw,
    dataLabel: i18n._(t`ángulo no calibrado bruto`),
    lastDataLabel: i18n._(t`último ángulo no calibrado bruto`),
  },
  [MetricField.BatterySoc]: {
    value: MetricField.BatterySoc,
    dataLabel: i18n._(t`estado de carga de batería`),
    lastDataLabel: i18n._(t`último estado de carga de batería`),
  },
  [MetricField.BatterySocRaw]: {
    value: MetricField.BatterySocRaw,
    dataLabel: i18n._(t`estado de carga de batería bruto`),
    lastDataLabel: i18n._(t`último estado de carga de batería bruto`),
  },
  [MetricField.BatteryV]: {
    value: MetricField.BatteryV,
    dataLabel: i18n._(t`voltaje de batería`),
    lastDataLabel: i18n._(t`último voltaje de batería`),
  },
  [MetricField.BatteryVRaw]: {
    value: MetricField.BatteryVRaw,
    dataLabel: i18n._(t`voltaje de batería bruto`),
    lastDataLabel: i18n._(t`último voltaje de batería bruto`),
  },
  [MetricField.CalculatedAngle]: {
    value: MetricField.CalculatedAngle,
    dataLabel: i18n._(t`ángulo calculado`),
    lastDataLabel: i18n._(t`último ángulo calculado`),
  },
  [MetricField.CalculatedTotalVolume]: {
    value: MetricField.CalculatedTotalVolume,
    dataLabel: i18n._(t`volumen total calculado`),
    lastDataLabel: i18n._(t`último volumen total calculado`),
  },
  [MetricField.CalculatedVolume]: {
    value: MetricField.CalculatedVolume,
    dataLabel: i18n._(t`volumen calculado`),
    lastDataLabel: i18n._(t`último volumen calculado`),
  },
  [MetricField.ChargeI]: {
    value: MetricField.ChargeI,
    dataLabel: i18n._(t`corriente de carga`),
    lastDataLabel: i18n._(t`última corriente de carga`),
  },
  [MetricField.ChargeIRaw]: {
    value: MetricField.ChargeIRaw,
    dataLabel: i18n._(t`corriente de carga bruta`),
    lastDataLabel: i18n._(t`última corriente de carga bruta`),
  },
  [MetricField.ChargeP]: {
    value: MetricField.ChargeP,
    dataLabel: i18n._(t`potencia de carga`),
    lastDataLabel: i18n._(t`última potencia de carga`),
  },
  [MetricField.ChargePRaw]: {
    value: MetricField.ChargePRaw,
    dataLabel: i18n._(t`potencia de carga bruta`),
    lastDataLabel: i18n._(t`última potencia de carga bruta`),
  },
  [MetricField.Conductivity]: {
    value: MetricField.Conductivity,
    dataLabel: i18n._(t`conductividad`),
    lastDataLabel: i18n._(t`última conductividad`),
  },
  [MetricField.ConductivityRaw]: {
    value: MetricField.ConductivityRaw,
    dataLabel: i18n._(t`conductividad bruta`),
    lastDataLabel: i18n._(t`última conductividad bruta`),
  },
  [MetricField.ConnectionState]: {
    value: MetricField.ConnectionState,
    dataLabel: i18n._(t`estado de conexión`),
    lastDataLabel: i18n._(t`último estado de conexión`),
  },
  [MetricField.DgaAccumulatedVolume]: {
    value: MetricField.DgaAccumulatedVolume,
    dataLabel: i18n._(t`volumen acumulado dga`),
    lastDataLabel: i18n._(t`último volumen acumulado dga`),
  },
  [MetricField.DgaFlow]: {
    value: MetricField.DgaFlow,
    dataLabel: i18n._(t`caudal dga`),
    lastDataLabel: i18n._(t`último caudal dga`),
  },
  [MetricField.DgaLevel]: {
    value: MetricField.DgaLevel,
    dataLabel: i18n._(t`nivel dga`),
    lastDataLabel: i18n._(t`último nivel dga`),
  },
  [MetricField.DissolvedOxygen]: {
    value: MetricField.DissolvedOxygen,
    dataLabel: i18n._(t`oxígeno disuelto`),
    lastDataLabel: i18n._(t`último oxígeno disuelto`),
  },
  [MetricField.DissolvedOxygenRaw]: {
    value: MetricField.DissolvedOxygenRaw,
    dataLabel: i18n._(t`oxígeno disuelto bruto`),
    lastDataLabel: i18n._(t`último oxígeno disuelto bruto`),
  },
  [MetricField.DissolvedOxygenSaturation]: {
    value: MetricField.DissolvedOxygenSaturation,
    dataLabel: i18n._(t`saturación de oxígeno disuelto`),
    lastDataLabel: i18n._(t`última saturación de oxígeno disuelto`),
  },
  [MetricField.DissolvedOxygenSaturationRaw]: {
    value: MetricField.DissolvedOxygenSaturationRaw,
    dataLabel: i18n._(t`saturación de oxígeno disuelto bruta`),
    lastDataLabel: i18n._(t`última saturación de oxígeno disuelto bruta`),
  },
  [MetricField.DistributionAlerts]: {
    value: MetricField.DistributionAlerts,
    dataLabel: i18n._(t`alertas de distribución`),
    lastDataLabel: i18n._(t`última alerta de distribución`),
  },
  [MetricField.EnabledRatingCurves]: {
    value: MetricField.EnabledRatingCurves,
    dataLabel: i18n._(t`curvas de aforo habilitadas`),
    lastDataLabel: i18n._(t`última curva de aforo habilitada`),
  },
  [MetricField.Energy]: {
    value: MetricField.Energy,
    dataLabel: i18n._(t`energía`),
    lastDataLabel: i18n._(t`última energía`),
  },
  [MetricField.Events]: {
    value: MetricField.Events,
    dataLabel: i18n._(t`eventos`),
    lastDataLabel: i18n._(t`último evento`),
  },
  [MetricField.Flow]: {
    value: MetricField.Flow,
    dataLabel: i18n._(t`caudal`),
    lastDataLabel: i18n._(t`último caudal`),
  },
  [MetricField.FlowAlerts]: {
    value: MetricField.FlowAlerts,
    dataLabel: i18n._(t`alertas de caudal`),
    lastDataLabel: i18n._(t`última alerta de caudal`),
  },
  [MetricField.FlowRaw]: {
    value: MetricField.FlowRaw,
    dataLabel: i18n._(t`caudal bruto`),
    lastDataLabel: i18n._(t`último caudal bruto`),
  },
  [MetricField.Level]: {
    value: MetricField.Level,
    dataLabel: i18n._(t`nivel`),
    lastDataLabel: i18n._(t`último nivel`),
  },
  [MetricField.LevelAlerts]: {
    value: MetricField.LevelAlerts,
    dataLabel: i18n._(t`alertas de nivel`),
    lastDataLabel: i18n._(t`última alerta de nivel`),
  },
  [MetricField.LevelRaw]: {
    value: MetricField.LevelRaw,
    dataLabel: i18n._(t`nivel bruto`),
    lastDataLabel: i18n._(t`último nivel bruto`),
  },
  [MetricField.OriginalDistance]: {
    value: MetricField.OriginalDistance,
    dataLabel: i18n._(t`distancia original`),
    lastDataLabel: i18n._(t`última distancia original`),
  },
  [MetricField.OriginalDistanceRaw]: {
    value: MetricField.OriginalDistanceRaw,
    dataLabel: i18n._(t`distancia original bruta`),
    lastDataLabel: i18n._(t`última distancia original bruta`),
  },
  [MetricField.OriginalVol]: {
    value: MetricField.OriginalVol,
    dataLabel: i18n._(t`volumen original`),
    lastDataLabel: i18n._(t`último volumen original`),
  },
  [MetricField.OriginalVolRaw]: {
    value: MetricField.OriginalVolRaw,
    dataLabel: i18n._(t`volumen original bruto`),
    lastDataLabel: i18n._(t`último volumen original bruto`),
  },
  [MetricField.Orp]: {
    value: MetricField.Orp,
    dataLabel: i18n._(t`orp`),
    lastDataLabel: i18n._(t`último orp`),
  },
  [MetricField.OrpRaw]: {
    value: MetricField.OrpRaw,
    dataLabel: i18n._(t`orp bruto`),
    lastDataLabel: i18n._(t`último orp bruto`),
  },
  [MetricField.Ph]: {
    value: MetricField.Ph,
    dataLabel: i18n._(t`ph`),
    lastDataLabel: i18n._(t`último ph`),
  },
  [MetricField.PhRaw]: {
    value: MetricField.PhRaw,
    dataLabel: i18n._(t`ph bruto`),
    lastDataLabel: i18n._(t`último ph bruto`),
  },
  [MetricField.Position]: {
    value: MetricField.Position,
    dataLabel: i18n._(t`posición`),
    lastDataLabel: i18n._(t`última posición`),
  },
  [MetricField.RatingCurvesLimits]: {
    value: MetricField.RatingCurvesLimits,
    dataLabel: i18n._(t`límites de curvas de aforo`),
    lastDataLabel: i18n._(t`último límite de curva de aforo`),
  },
  [MetricField.SignalNoiseRatio]: {
    value: MetricField.SignalNoiseRatio,
    dataLabel: i18n._(t`relación señal ruido`),
    lastDataLabel: i18n._(t`última relación señal ruido`),
  },
  [MetricField.SignalQuality]: {
    value: MetricField.SignalQuality,
    dataLabel: i18n._(t`calidad de señal`),
    lastDataLabel: i18n._(t`última calidad de señal`),
  },
  [MetricField.SignalStrength]: {
    value: MetricField.SignalStrength,
    dataLabel: i18n._(t`intensidad de señal`),
    lastDataLabel: i18n._(t`última intensidad de señal`),
  },
  [MetricField.SnowLevel]: {
    value: MetricField.SnowLevel,
    dataLabel: i18n._(t`nivel de nieve`),
    lastDataLabel: i18n._(t`último nivel de nieve`),
  },
  [MetricField.SnowLevelNonCalibrated]: {
    value: MetricField.SnowLevelNonCalibrated,
    dataLabel: i18n._(t`nivel de nieve no calibrado`),
    lastDataLabel: i18n._(t`último nivel de nieve no calibrado`),
  },
  [MetricField.SnowLevelRaw]: {
    value: MetricField.SnowLevelRaw,
    dataLabel: i18n._(t`nivel de nieve bruto`),
    lastDataLabel: i18n._(t`último nivel de nieve bruto`),
  },
  [MetricField.SnowRadiation]: {
    value: MetricField.SnowRadiation,
    dataLabel: i18n._(t`radiación de nieve`),
    lastDataLabel: i18n._(t`última radiación de nieve`),
  },
  [MetricField.SnowRadiationRaw]: {
    value: MetricField.SnowRadiationRaw,
    dataLabel: i18n._(t`radiación de nieve bruta`),
    lastDataLabel: i18n._(t`última radiación de nieve bruta`),
  },
  [MetricField.SnowTemperature]: {
    value: MetricField.SnowTemperature,
    dataLabel: i18n._(t`temperatura de nieve`),
    lastDataLabel: i18n._(t`última temperatura de nieve`),
  },
  [MetricField.SnowTemperatureRaw]: {
    value: MetricField.SnowTemperatureRaw,
    dataLabel: i18n._(t`temperatura de nieve bruta`),
    lastDataLabel: i18n._(t`última temperatura de nieve bruta`),
  },
  [MetricField.SnowWaterEquivalent]: {
    value: MetricField.SnowWaterEquivalent,
    dataLabel: i18n._(t`equivalente de agua de nieve`),
    lastDataLabel: i18n._(t`último equivalente de agua de nieve`),
  },
  [MetricField.SnowWaterEquivalentNonCalibrated]: {
    value: MetricField.SnowWaterEquivalentNonCalibrated,
    dataLabel: i18n._(t`equivalente de agua de nieve no calibrado`),
    lastDataLabel: i18n._(t`último equivalente de agua de nieve no calibrado`),
  },
  [MetricField.SnowWaterEquivalentRaw]: {
    value: MetricField.SnowWaterEquivalentRaw,
    dataLabel: i18n._(t`equivalente de agua de nieve bruto`),
    lastDataLabel: i18n._(t`último equivalente de agua de nieve bruto`),
  },
  [MetricField.SnowWeight]: {
    value: MetricField.SnowWeight,
    dataLabel: i18n._(t`peso de nieve`),
    lastDataLabel: i18n._(t`último peso de nieve`),
  },
  [MetricField.SnowWeightRaw]: {
    value: MetricField.SnowWeightRaw,
    dataLabel: i18n._(t`peso de nieve bruto`),
    lastDataLabel: i18n._(t`último peso de nieve bruto`),
  },
  [MetricField.SolarI]: {
    value: MetricField.SolarI,
    dataLabel: i18n._(t`corriente solar`),
    lastDataLabel: i18n._(t`última corriente solar`),
  },
  [MetricField.SolarIRaw]: {
    value: MetricField.SolarIRaw,
    dataLabel: i18n._(t`corriente solar bruta`),
    lastDataLabel: i18n._(t`última corriente solar bruta`),
  },
  [MetricField.SolarP]: {
    value: MetricField.SolarP,
    dataLabel: i18n._(t`potencia solar`),
    lastDataLabel: i18n._(t`última potencia solar`),
  },
  [MetricField.SolarPRaw]: {
    value: MetricField.SolarPRaw,
    dataLabel: i18n._(t`potencia solar bruta`),
    lastDataLabel: i18n._(t`última potencia solar bruta`),
  },
  [MetricField.SolarV]: {
    value: MetricField.SolarV,
    dataLabel: i18n._(t`voltaje solar`),
    lastDataLabel: i18n._(t`último voltaje solar`),
  },
  [MetricField.SolarVRaw]: {
    value: MetricField.SolarVRaw,
    dataLabel: i18n._(t`voltaje solar bruto`),
    lastDataLabel: i18n._(t`último voltaje solar bruto`),
  },
  [MetricField.StoredVolume]: {
    value: MetricField.StoredVolume,
    dataLabel: i18n._(t`volumen almacenado`),
    lastDataLabel: i18n._(t`último volumen almacenado`),
  },
  [MetricField.Temperature]: {
    value: MetricField.Temperature,
    dataLabel: i18n._(t`temperatura`),
    lastDataLabel: i18n._(t`última temperatura`),
  },
  [MetricField.TemperatureRaw]: {
    value: MetricField.TemperatureRaw,
    dataLabel: i18n._(t`temperatura bruta`),
    lastDataLabel: i18n._(t`última temperatura bruta`),
  },
  [MetricField.TurbidityFnu]: {
    value: MetricField.TurbidityFnu,
    dataLabel: i18n._(t`turbidez fnu`),
    lastDataLabel: i18n._(t`última turbidez fnu`),
  },
  [MetricField.TurbidityFnuRaw]: {
    value: MetricField.TurbidityFnuRaw,
    dataLabel: i18n._(t`turbidez fnu bruta`),
    lastDataLabel: i18n._(t`última turbidez fnu bruta`),
  },
  [MetricField.TurbidityNtu]: {
    value: MetricField.TurbidityNtu,
    dataLabel: i18n._(t`turbidez ntu`),
    lastDataLabel: i18n._(t`última turbidez ntu`),
  },
  [MetricField.TurbidityNtuRaw]: {
    value: MetricField.TurbidityNtuRaw,
    dataLabel: i18n._(t`turbidez ntu bruta`),
    lastDataLabel: i18n._(t`última turbidez ntu bruta`),
  },
  [MetricField.Velocity]: {
    value: MetricField.Velocity,
    dataLabel: i18n._(t`velocidad`),
    lastDataLabel: i18n._(t`última velocidad`),
  },
  [MetricField.VelocityRaw]: {
    value: MetricField.VelocityRaw,
    dataLabel: i18n._(t`velocidad bruta`),
    lastDataLabel: i18n._(t`última velocidad bruta`),
  },
  [MetricField.VolumeDay]: {
    value: MetricField.VolumeDay,
    dataLabel: i18n._(t`volumen diario`),
    lastDataLabel: i18n._(t`último volumen diario`),
  },
  [MetricField.VolumeHour]: {
    value: MetricField.VolumeHour,
    dataLabel: i18n._(t`volumen horario`),
    lastDataLabel: i18n._(t`último volumen horario`),
  },
  [MetricField.VolumeM3]: {
    value: MetricField.VolumeM3,
    dataLabel: i18n._(t`volumen`),
    lastDataLabel: i18n._(t`último volumen`),
  },
  [MetricField.VolumeMonth]: {
    value: MetricField.VolumeMonth,
    dataLabel: i18n._(t`volumen mensual`),
    lastDataLabel: i18n._(t`último volumen mensual`),
  },
});

export const getMetricFieldAttributes = (key: MetricField, i18n: I18n): MetricFieldAttributes => {
  const attributesMap = getAttributesMap(i18n);
  if (!(key in attributesMap)) {
    throw new Error(`La clave ${key} no se encuentra en el mapa de atributos de MetricField`);
  }
  return attributesMap[key];
};

export const getAllMetricFieldAttributes = (i18n: I18n): MetricFieldAttributes[] => {
  const attributesMap = getAttributesMap(i18n);
  return Object.values(attributesMap);
};
